import React from "react";
import { Star, ShieldCheck } from "lucide-react";
import { inr } from "../../lib/api";

// Safety card shown to the customer once a driver is assigned: driver photo,
// vehicle photo, plate and make/model so they can verify who's picking them up.
export default function DriverSafetyCard({ driver, ride }) {
  if (!driver) return null;
  const vehicle = driver.vehicle || driver.vehicle_model || ride?.vehicle_type || "";
  const plate = driver.plate || driver.registration;
  const surge = ride?.traffic_surge_applied;
  return (
    <div className="rounded-2xl border border-ink-100 bg-white p-3" data-testid="driver-safety-card">
      <div className="mb-1 flex items-center gap-1.5 text-xs font-semibold text-emerald-700">
        <ShieldCheck className="h-3.5 w-3.5" /> Verify your ride
      </div>
      <div className="flex items-center gap-3">
        {driver.photo ? (
          <img src={driver.photo} alt={driver.name} className="h-14 w-14 shrink-0 rounded-full border-2 border-emerald-500 object-cover" data-testid="safety-driver-photo" onError={(e) => { e.currentTarget.style.display = "none"; }} />
        ) : (
          <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-graphite text-xl font-bold text-white">{driver.name?.[0]?.toUpperCase() || "D"}</span>
        )}
        <div className="min-w-0 flex-1">
          <p className="truncate text-base font-bold text-ink" data-testid="safety-driver-name">{driver.name}</p>
          {vehicle && <p className="truncate text-sm text-ink-soft" data-testid="safety-vehicle">{vehicle}</p>}
          {typeof driver.rating === "number" && (
            <p className="mt-0.5 flex items-center gap-1 text-xs text-ink-muted"><Star className="h-3 w-3 fill-sunny-500 text-sunny-500" /> {driver.rating.toFixed(1)}{driver.trips ? ` · ${driver.trips} trips` : ""}</p>
          )}
        </div>
        {(driver.vehicle_photo || plate) && (
          <div className="flex shrink-0 flex-col items-center gap-1" data-testid="safety-vehicle-block">
            {driver.vehicle_photo && (
              <img src={driver.vehicle_photo} alt="Vehicle" className="h-16 w-24 rounded-lg border border-ink-100 object-cover" data-testid="safety-vehicle-photo" onError={(e) => { e.currentTarget.style.display = "none"; }} />
            )}
            {plate && <p className="rounded-md bg-ink px-2 py-0.5 text-sm font-bold tracking-wider text-white" data-testid="safety-plate">{plate}</p>}
          </div>
        )}
      </div>
      {plate && (
        <p className="mt-2 flex items-center gap-1.5 rounded-lg bg-emerald-50 px-3 py-2 text-xs font-medium text-emerald-800" data-testid="safety-plate-match">
          <ShieldCheck className="h-3.5 w-3.5 shrink-0" /> Match the number plate with the cab before you board.
        </p>
      )}
      {surge && (
        <p className="mt-2 rounded-lg bg-amber-50 px-3 py-2 text-xs font-medium text-amber-700" data-testid="safety-traffic-surge">
          Heavy traffic on your route — fare adjusted to {inr(ride.fare)} ({ride.traffic_surge_mult || 1.3}× traffic surge{ride.traffic_surge_amount ? `, +${inr(ride.traffic_surge_amount)}` : ""}). This extra amount goes entirely to your driver — KohimaTaxi takes no profit from surge.
        </p>
      )}
    </div>
  );
}
